import { Checkbox, EmptyState, InfoIcon, ModalBottomButtons } from '@bp/ui-components';
import classNames from 'classnames';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePreviousSchoolYear, useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  useCreateTeachingLoadFactorMutation,
  useTeachingLoadFactorsQuery,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessToast, showUserErrorToast } from '../../../utils/toast';
import { TeachingLoadFactorType } from '../types/factorTypes';
import styles from './FactorInheritForm.module.scss';
import { FactorInheritFormRow } from './FactorInheritFormRow';

export type FactorInheritFormType = (TeachingLoadFactorType & {
  selected: boolean;
})[];

type FactorInheritFormProps = {
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const FactorInheritForm = ({ onLoad, onClose }: FactorInheritFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();
  const previousSchoolYear = usePreviousSchoolYear();
  const factorContext = useMemorizedCacheTag('TEACHINGLOADFACTOR');

  const [, createTeachingLoadFactor] = useCreateTeachingLoadFactorMutation();

  const [{ data: factorsData }] = useTeachingLoadFactorsQuery({
    variables: { schoolYearUuid: previousSchoolYear?.uuid ?? '' },
    context: factorContext,
    pause: !previousSchoolYear,
  });

  const lastYearFactors: FactorInheritFormType =
    factorsData?.teachingLoadFactors.map((factor) => {
      return {
        ...factor,
        selected: false,
      };
    }) ?? [];

  async function handleSubmit(values: FactorInheritFormType, helper: FormikHelpers<FactorInheritFormType>) {
    onLoad(true);

    const factorsToInherit = values.filter((f) => f.selected);

    let inheritCount = 0;

    for (const factor of factorsToInherit) {
      const { error } = await createTeachingLoadFactor(
        {
          input: {
            schoolYearUuid: currentSchoolYear?.uuid ?? '',
            factorType: factor.factorType,
            grades: factor.grades,
            gradeGroupUuids: factor.gradeGroup.map((item) => item.uuid),
            subjectUuids: factor.subject.map((item) => item.uuid),
            subjectContainerUuids: factor.subjectContainer.map((item) => item.uuid),
            timeGridEntryUuids: factor.timeGridEntries.map((item) => item.uuid),
            teacherUuids: factor.teachers.map((item) => item.uuid),
            value: factor.value,
            valueRaw: factor.valueRaw,
            valueMode: factor.valueMode,
            comment: factor.comment,
            active: factor.active,
            valuePartial: factor.valuePartial,
            valuePartialRaw: factor.valuePartialRaw,
            // Die Schülerzahl-Grenzen gehören zum Faktor und ziehen mit ins neue Schuljahr.
            studentsMin: factor.studentsMin,
            studentsMax: factor.studentsMax,
            studentsPartialMin: factor.studentsPartialMin,
            studentsPartialMax: factor.studentsPartialMax,
          },
        },
        factorContext,
      );

      if (error) {
        showUserErrorToast({ error });
        break;
      } else {
        inheritCount++;
      }
    }

    if (inheritCount > 0) {
      showSuccessToast(t('factors.inheritSuccess', { count: inheritCount }));
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  }

  return !previousSchoolYear || lastYearFactors.length === 0 ? (
    <EmptyState
      title={!previousSchoolYear ? t('common.noPreviousYear') : t('factors.noPreviousFactors')}
      icon={<InfoIcon />}
    />
  ) : (
    <Formik<FactorInheritFormType> initialValues={lastYearFactors} onSubmit={handleSubmit}>
      {({ values, isSubmitting, resetForm, setValues }) => {
        return (
          <Form className={styles['factor-inherit-form']}>
            <div className={styles.header}>
              <div className={styles.checkbox}>
                <Checkbox
                  name='select-all'
                  checked={values.filter((f) => f.selected).length === values.length}
                  onChange={async () => {
                    let selected = false;
                    if (values.filter((f) => f.selected).length === values.length) {
                      selected = false;
                    } else {
                      selected = true;
                    }
                    await setValues((values) =>
                      values.map((f) => {
                        return { ...f, selected };
                      }),
                    );
                  }}
                />
              </div>
              <div className={styles.small}>{t('common.type')}</div>
              <div className={styles.small}>{t('classes.level.full')}</div>
              <div className={styles.small}>{t('gradeGroups.title', { count: 1 })}</div>
              <div className={styles.large}>{t('subject.name')}</div>
              <div className={styles.medium}>{t('subjectContainer.title', { count: 1 })}</div>
              <div className={styles.small}>{t('common.timeGrids')}</div>
              <div className={styles.small}>{t('persons.title', { count: 1 })}</div>
              <div className={styles.small}>{t('factors.title', { count: 1 })}</div>
              <div className={styles.small}>{t('factors.titlePartial')}</div>
              <div className={styles.comment}>{t('common.comment')}</div>
            </div>

            <div className={classNames(styles.body, 'has-scrollbar')}>
              <FieldArray
                name='factors'
                render={(arrayHelpers) => {
                  return values.map((factor, index) => (
                    <FactorInheritFormRow key={factor.uuid} index={index} helper={arrayHelpers} />
                  ));
                }}
              />
            </div>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: isSubmitting || values.filter((f) => f.selected).length === 0 }}
              className='px-6'
            />
          </Form>
        );
      }}
    </Formik>
  );
};
